import { useTranslation } from "react-i18next";
import { ErrorBox, Skeleton } from "../../components/ui";
import { useAppraisalBoard } from "../../lib/api";
import { SectionCard, Share, Stat } from "../labour/shared";

/** How the scores fall, and the merit input the incentive path lacks (HRX-08).
 *
 * Two things are worth showing that an average hides.
 *
 * A distribution where everybody "meets" is the normal failure of a first appraisal cycle, and it is invisible
 * in a mean.
 *
 * And the platform's own final score is the plain mean of goals, self-assessment and colleague feedback —
 * **divided by three whether or not feedback exists**. Somebody scored 4.73 by their manager and 4.93 by
 * themselves lands at 3.22 because nobody was asked. That is a scoring artefact rather than a judgement, so
 * both figures are shown: a voice that never spoke is dropped and the rest re-weighted, and the difference
 * between the two is on the screen to be argued with rather than quietly corrected. */
export default function Scoring({ company }: { company: string }) {
  const { t } = useTranslation();
  const q = useAppraisalBoard(undefined, company || undefined);

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 grid gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;
  const top = Math.max(...d.distribution.map((b) => b.count), 1);
  const failing = d.state.filter((s) => !s.ok);

  return (
    <div className="mt-4 space-y-4" data-appraisals-scoring>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-appraisals-scoring-strip>
        <Stat label={t("apr.blendedAverage")} value={d.scoring.blended_average.toFixed(2)}
          hint={t("apr.blendedAverageHint")} />
        <Stat label={t("apr.platformAverage")} value={d.scoring.platform_average.toFixed(2)}
          cls="text-status-amber" hint={t("apr.platformAverageHint")} />
        <Stat label={t("apr.reweightedN")} value={d.scoring.reweighted}
          hint={t("apr.reweightedNHint")} />
        <Stat label={t("apr.meritAvailable")} value={d.merit.available}
          cls={d.merit.consumed_by_incentive ? "text-status-green" : "text-status-amber"}
          hint={t("apr.meritHint", { n: d.merit.incentive_plans })} />
      </div>

      <div className="rounded border border-status-amber/40 bg-amber-50 px-4 py-3 text-sm text-status-amber"
        data-appraisals-divide-note>
        {t("apr.divideByThree")}
      </div>

      <SectionCard title={t("apr.distribution")} aside={t("apr.distributionHint")}>
        <table className="w-full text-sm">
          <tbody>
            {d.distribution.map((b) => (
              <tr key={b.label} className="border-t border-brand-grey-light first:border-t-0"
                data-appraisals-band={b.label}>
                <td className="w-40 px-4 py-2 font-medium">{t(`apr.band.${b.label}`)}</td>
                <td className="px-2 py-2 text-[11px] text-brand-grey">{t("apr.fromScore", { n: b.from_score })}</td>
                <td className="px-2 py-2"><div className="max-w-xs"><Share value={b.count} of={top} /></div></td>
                <td className="w-16 px-2 py-2 text-right font-semibold tabular-nums">{b.count}</td>
                <td className="w-28 px-4 py-2 text-right text-[11px] text-brand-grey">
                  {t("apr.multiplier", { n: b.merit_multiplier })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>

      <SectionCard title={t("apr.checks")} aside={t("apr.checksN", { n: failing.length, of: d.state.length })}>
        <ul>
          {d.state.map((s) => (
            <li key={s.key} className="flex items-start gap-2 border-t border-brand-grey-light px-4 py-3 first:border-t-0"
              data-appraisals-check={s.key} data-appraisals-check-ok={s.ok ? "1" : "0"}>
              <span className={`mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full ${s.ok ? "bg-status-green" : "bg-status-amber"}`} />
              <div>
                <div className="font-semibold">{t(`apr.check.${s.key}.title`)}</div>
                <p className="mt-0.5 max-w-4xl text-sm text-brand-grey">
                  {t(`apr.check.${s.key}.${s.ok ? "ok" : "bad"}`, s.facts) as string}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </SectionCard>
    </div>
  );
}
